<template lang="pug">
    el-drawer.full-height-drawer.template-drawer(
        :visible="visible"
        :with-header="true"
        :title="form.id ? $t('Edit Template') : $t('New Template')"
        direction="rtl"
        size="720px"
        :close-on-press-escape="!testing.running"
        :wrapper-closable="false"
        @update:visible="value => $emit('update:visible', value)"
        @open="onOpen"
        @close="onClose"
    )
        .drawer-body.no-tabs(v-loading="loadingModels")
            .td-steps
                //- ===== STEP 1: describe the template =====
                section.td-step
                    .td-step__marker 1
                    .td-step__content
                        h4.td-step__title {{ $t('Describe') }}
                        .td-field
                            label.td-label {{ $t('Name') }}
                                span.td-required *
                            el-input(v-model="form.name" :maxlength="120" show-word-limit :placeholder="$t('e.g. Model in studio')")
                        .td-field
                            label.td-label {{ $t('What should this template do?') }}
                                span.td-required *
                            el-input(
                                v-model="form.intent"
                                type="textarea"
                                :autosize="{ minRows: 3 }"
                                :maxlength="2000"
                                show-word-limit
                                :placeholder="$t('Describe the goal in your own words. The AI phrases the final prompt from it.')"
                            )
                        .td-field(v-if="shopEnabled")
                            label.td-label {{ $t('Scope') }}
                            el-radio-group(v-model="form.context" size="small" @change="onContextChange")
                                el-radio-button(label="auto") {{ $t('Automatic') }}
                                el-radio-button(label="product") {{ $t('Product image') }}
                                el-radio-button(label="general") {{ $t('General') }}
                            transition(name="td-fade")
                                .td-detected(v-if="form.context === 'auto' && resolvedContext")
                                    i.el-icon-fa-wand-magic-sparkles.fas
                                    | {{ $t('detected') }}: {{ resolvedContext === 'product' ? $t('Product image') : $t('General') }}

                //- ===== STEP 2: reference images (the positional convention,
                //- made visible: every card carries its position badge) =====
                section.td-step
                    .td-step__marker 2
                    .td-step__content
                        h4.td-step__title {{ $t('Reference images') }}
                            span.td-step__optional {{ $t('optional') }}
                        .td-refs
                            transition-group(name="td-fade" tag="div" class="td-refs__list")
                                .td-ref(v-for="(reference, index) in form.references" :key="referenceKey(reference)")
                                    .td-ref__visual
                                        img.td-ref__thumb(:src="referenceThumb(reference.file)")
                                        span.td-ref__badge {{ $t('Image') }} {{ index + 2 }}
                                    .td-ref__body
                                        el-input(
                                            v-model="reference.role"
                                            size="small"
                                            :maxlength="120"
                                            :placeholder="$t('Role, e.g. model wearing the product')"
                                        )
                                        .td-ref__name {{ reference.file.name }}
                                    .td-ref__actions
                                        el-button(circle plain size="mini" icon="el-icon-arrow-up" :disabled="index === 0" @click="moveReference(index, -1)")
                                        el-button(circle plain size="mini" icon="el-icon-arrow-down" :disabled="index === form.references.length - 1" @click="moveReference(index, 1)")
                                        el-button(circle plain size="mini" type="danger" icon="el-icon-fa-trash-alt far" @click="removeReference(index)")
                            button.td-ref-add(
                                v-if="form.references.length < 4"
                                type="button"
                                @click="openLibrary('reference')"
                            )
                                i.el-icon-picture-outline
                                span {{ $t('Add reference from library') }}
                        p.td-hint {{ $t('References are sent along with the edited image, e.g. a model and a background for consistent "same model, same scene" results. The first image is always the one being edited.') }}
                        transition(name="td-fade")
                            .td-alert(v-if="promptStale")
                                i.el-icon-warning-outline
                                | {{ $t('References changed, rephrase the prompt.') }}

                //- ===== STEP 3: phrase + review the prompt, pick the model =====
                section.td-step
                    .td-step__marker 3
                    .td-step__content
                        h4.td-step__title {{ $t('Prompt') }}
                        .td-field.td-field--model
                            label.td-label {{ $t('Model') }}
                            el-select(v-model="form.model_key" style="width: 100%" clearable popper-class="model-select-dropdown" :placeholder="$t('Default model of the CMS')")
                                el-option(
                                    v-for="model in modelOptions"
                                    :key="model.key"
                                    :value="model.key"
                                    :label="modelLabel(model)"
                                )
                                    model-select-option(:model="model")
                            p.td-hint(v-if="form.references.length") {{ $t('Templates with reference images need a model with multi-image input.') }}
                        //- Cost levers the chosen model offers. Pinned here, they preset
                        //- the controls every time this template is applied — exactly like
                        //- model_key. Left empty, the shop default applies instead.
                        .td-field(v-if="qualityOptions.length > 1")
                            label.td-label {{ $t('Quality') }}
                            el-select(v-model="form.quality" style="width: 100%" clearable :placeholder="$t('Default of the CMS')")
                                el-option(
                                    v-for="option in qualityOptions"
                                    :key="option"
                                    :value="option"
                                    :label="$t('quality.' + option)"
                                )
                        .td-field(v-if="resolutionCapOptions.length > 1")
                            label.td-label {{ $t('Maximum edge length') }}
                            el-select(v-model="form.resolution_cap" style="width: 100%" clearable :placeholder="$t('Default of the CMS')")
                                el-option(
                                    v-for="option in resolutionCapOptions"
                                    :key="option"
                                    :value="option"
                                    :label="option + 'px'"
                                )
                            p.td-hint {{ $t('Limits the result to at most this size on its longer edge, without upscaling a smaller source.') }}
                        button.td-phrase(
                            type="button"
                            :disabled="!canPhrase"
                            :class="{ 'is-busy': phrasing }"
                            @click="phrase(false)"
                        )
                            i.el-icon-fa-wand-magic-sparkles.fas
                            span {{ phrasing ? $t('Phrasing…') : $t('Phrase prompt') }}
                        transition(name="td-fade")
                            .td-prompt(v-if="form.prompt !== ''")
                                el-input(
                                    v-model="form.prompt"
                                    v-loading="phrasing"
                                    type="textarea"
                                    :autosize="{ minRows: 4 }"
                                )
                                .td-prompt__foot
                                    span.td-hint {{ $t('Review and edit the prompt, it is used exactly as written.') }}
                                    button.td-ghost(
                                        type="button"
                                        :disabled="!canPhrase"
                                        @click="phrase(true)"
                                    )
                                        i.el-icon-refresh
                                        | {{ $t('Rephrase') }}

                //- ===== STEP 4: test bench, a real billed generation in preview
                //- mode, sample in, result out, nothing attached to the media =====
                section.td-step.td-step--last
                    .td-step__marker 4
                    .td-step__content
                        h4.td-step__title {{ $t('Test') }}
                        .td-bench(:class="{ 'is-running': testing.running }")
                            .td-bench__pane(@click="openLibrary('sample')")
                                span.td-bench__tag {{ $t('Sample image') }}
                                img(v-if="sample" :src="sample.url")
                                .td-bench__empty(v-else)
                                    i.el-icon-picture-outline
                                    span {{ $t('Select from library') }}
                            .td-bench__arrow
                                i.el-icon-right
                            .td-bench__pane.td-bench__pane--result(
                                :class="{ 'is-zoomable': testing.previewUrl }"
                                @click="openResultLightbox"
                            )
                                span.td-bench__tag {{ $t('Result') }}
                                template(v-if="testing.previewUrl")
                                    img(:src="testing.previewUrl" @load="onResultLoad")
                                    .td-bench__zoom
                                        i.el-icon-zoom-in
                                        span {{ $t('View full size') }}
                                .td-bench__progress(v-else-if="testing.running")
                                    el-progress(type="circle" :percentage="testing.progress || 0" :width="64" :stroke-width="4")
                                .td-bench__empty(v-else)
                                    i.el-icon-fa-wand-magic-sparkles.fas
                        .td-bench-actions
                            el-button(
                                type="primary"
                                plain
                                icon="el-icon-video-play"
                                :loading="testing.running"
                                :disabled="!canTest"
                                @click="startTest"
                            ) {{ $t('Run test') }}
                            .td-cost(v-if="selectedModelPrice")
                                span.td-cost__label {{ $t('Cost per test') }}
                                span.td-cost__amount {{ selectedModelPrice }}
                        p.td-hint {{ $t('The test runs a real, billed generation on the sample image, without attaching anything to your media.') }}

        .drawer-footer
            el-button(
                round
                size="mini"
                icon="el-icon-fa-times fas"
                @click="$emit('update:visible', false)"
            ) {{ $t('Close') }}
            el-button(
                round
                size="mini"
                type="primary"
                icon="el-icon-fa-check fas"
                :loading="saving"
                :disabled="!canSave"
                @click="save(true)"
            ) {{ $t('Save') }}

        ImageLibraryDialog(
            :visible.sync="libraryDialogVisible"
            :multiple="false"
            selection-type="image"
            @select="onLibrarySelect"
        )

        PhotoSwipeGallery(
            :images="resultGalleryImages"
            :open.sync="lightboxOpen"
        )
</template>

<i18n>
{
    "en": {
        "unit.images": "image | images",
        "quality.low": "Low",
        "quality.medium": "Medium",
        "quality.high": "High"
    },
    "de": {
        "up to": "bis zu",
        "unit.images": "Bild | Bilder",
        "New Template": "Neue Vorlage",
        "Edit Template": "Vorlage bearbeiten",
        "Describe": "Beschreiben",
        "Name": "Name",
        "e.g. Model in studio": "z. B. Model im Studio",
        "Scope": "Anwendungsbereich",
        "Automatic": "Automatisch",
        "Product image": "Produktbild",
        "General": "Allgemein",
        "detected": "erkannt",
        "What should this template do?": "Was soll diese Vorlage tun?",
        "Describe the goal in your own words. The AI phrases the final prompt from it.": "Beschreiben Sie das Ziel in eigenen Worten. Die KI formuliert daraus den finalen Prompt.",
        "Reference images": "Referenzbilder",
        "optional": "optional",
        "Role, e.g. model wearing the product": "Rolle, z. B. Model, das das Produkt trägt",
        "Image": "Bild",
        "Add reference from library": "Referenz aus Mediathek hinzufügen",
        "References are sent along with the edited image, e.g. a model and a background for consistent \"same model, same scene\" results. The first image is always the one being edited.": "Referenzen werden zusammen mit dem zu bearbeitenden Bild gesendet, z. B. ein Model und ein Hintergrund für konsistente \"gleiches Model, gleiche Szene\"-Ergebnisse. Das erste Bild ist immer das zu bearbeitende.",
        "References changed, rephrase the prompt.": "Referenzen geändert, Prompt neu formulieren.",
        "Phrase prompt": "Prompt formulieren",
        "Phrasing…": "Wird formuliert…",
        "Prompt": "Prompt",
        "Review and edit the prompt, it is used exactly as written.": "Prompt prüfen und bearbeiten, er wird exakt so verwendet.",
        "Rephrase": "Neu formulieren",
        "Model": "Modell",
        "Default model of the CMS": "Standardmodell des CMS",
        "Quality": "Qualität",
        "quality.low": "Niedrig",
        "quality.medium": "Mittel",
        "quality.high": "Hoch",
        "Maximum edge length": "Maximale Kantenlänge",
        "Default of the CMS": "Standard des CMS",
        "Limits the result to at most this size on its longer edge, without upscaling a smaller source.": "Begrenzt das Ergebnis auf höchstens diese Größe an der längeren Kante, ohne eine kleinere Quelle zu vergrößern.",
        "Templates with reference images need a model with multi-image input.": "Vorlagen mit Referenzbildern benötigen ein Modell mit Mehrbild-Eingabe.",
        "Test": "Test",
        "Sample image": "Beispielbild",
        "Select from library": "Aus Mediathek wählen",
        "Result": "Ergebnis",
        "View full size": "In voller Größe ansehen",
        "Run test": "Test starten",
        "Cost per test": "Kosten pro Test",
        "The test runs a real, billed generation on the sample image, without attaching anything to your media.": "Der Test führt eine echte, kostenpflichtige Generierung am Beispielbild aus, ohne etwas an Ihre Medien anzuhängen.",
        "Close": "Schließen",
        "Save": "Speichern",
        "Template saved": "Vorlage gespeichert",
        "Template could not be saved": "Vorlage konnte nicht gespeichert werden",
        "Prompt could not be phrased": "Prompt konnte nicht formuliert werden",
        "Test failed": "Test fehlgeschlagen",
        "Test completed": "Test abgeschlossen",
        "An error occurred": "Ein Fehler ist aufgetreten"
    }
}
</i18n>

<script>
import _ from 'lodash';

import Echo from '@/echo';
import { moneyFormat, preciseMoneyFormat } from '@/filters';
import { useSetupStore, usePromptTemplateStore } from '@/stores';

import api from '@/extensions/generative-media/store/api/generativeMedia';
import { proxiedFileUrl } from '@/extensions/generative-media/imageThumbnail';
import ImageLibraryDialog from '@/components/Page/Sections/Partials/ImageLibraryDialog';
import PhotoSwipeGallery from '@/components/PhotoSwipeGallery';
import ModelSelectOption from '@/extensions/generative-media/components/ModelSelectOption';

export default {
    name: 'TemplateDrawer',

    components: {
        ImageLibraryDialog,
        PhotoSwipeGallery,
        ModelSelectOption,
    },

    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        template: {
            type: Object,
            default: null,
        },
    },

    data() {
        return {
            form: this.emptyForm(),
            resolvedContext: null,
            // Signature of the references the current prompt was phrased with —
            // drives the "references changed, rephrase" staleness warning.
            phrasedReferencesSignature: null,
            models: [],
            loadingModels: false,
            phrasing: false,
            saving: false,
            testing: {
                running: false,
                progress: 0,
                previewUrl: null,
                previewWidth: null,
                previewHeight: null,
            },
            sample: null,
            // Lever-aware ceiling, fetched async like AiEditControls; the flat
            // effectivePrice is shown until it arrives.
            fetchedCeiling: null,
            ceilingRequestToken: 0,
            libraryTarget: null,
            libraryDialogVisible: false,
            channelName: null,
            lightboxOpen: false,
        };
    },

    computed: {
        setupStore() {
            return useSetupStore();
        },
        promptTemplateStore() {
            return usePromptTemplateStore();
        },
        cmsId() {
            // GraphQL delivers the id as a string, every Int arg needs a number.
            return parseInt(_.get(this.setupStore.activeCms, 'id'), 10) || null;
        },
        shopEnabled() {
            return this.setupStore.enabledExtensions.includes('shop');
        },
        // Templates with references need a multi-image model (image_urls array).
        modelOptions() {
            if (!this.form.references.length) return this.models;
            return this.models.filter(model => model.supportsReferences);
        },
        selectedModel() {
            return this.models.find(entry => entry.key === this.form.model_key) || null;
        },
        // Only the levers the pinned model actually declares (see EX_GM_Model).
        qualityOptions() {
            return _.get(this.selectedModel, 'qualityOptions') || [];
        },
        resolutionCapOptions() {
            return _.get(this.selectedModel, 'resolutionCapOptions') || [];
        },
        selectedModelPrice() {
            const model = this.selectedModel || this.models[0];
            const price = this.fetchedCeiling !== null
                ? this.fetchedCeiling
                : _.get(model, 'effectivePrice');
            if (!price) return null;
            return moneyFormat(price, 'EUR');
        },
        referencesSignature() {
            return JSON.stringify(this.form.references.map(reference => ({
                id: _.get(reference, 'file.id'),
                role: (reference.role || '').trim(),
            })));
        },
        promptStale() {
            return this.form.prompt !== ''
                && this.phrasedReferencesSignature !== null
                && this.phrasedReferencesSignature !== this.referencesSignature;
        },
        canPhrase() {
            if (this.phrasing) return false;
            if (!(this.form.intent || '').trim()) return false;
            return this.form.references.every(reference => (reference.role || '').trim() !== '');
        },
        canTest() {
            return !this.testing.running
                && (this.form.prompt || '').trim() !== ''
                && !!this.sample
                && (this.form.name || '').trim() !== '';
        },
        canSave() {
            return !this.saving && (this.form.name || '').trim() !== '';
        },
        resultGalleryImages() {
            if (!this.testing.previewUrl) return [];
            return [{
                src: this.testing.previewUrl,
                width: this.testing.previewWidth || _.get(this.sample, 'width') || 1200,
                height: this.testing.previewHeight || _.get(this.sample, 'height') || 1600,
                alt: this.form.name || '',
            }];
        },
    },

    watch: {
        // Adding a reference filters single-image models (e.g. FLUX Kontext) out of
        // the picker. If the current selection just dropped out, switch to the first
        // still-valid model so the field never holds an unusable model (mirrors the
        // image-edit drawer's auto-switch).
        modelOptions(options) {
            if (this.form.model_key && !options.some(model => model.key === this.form.model_key)) {
                this.form.model_key = options.length ? options[0].key : null;
            }
        },
        // A lever value the NEW model doesn't offer would be rejected on save (and
        // silently ignored at generation time), so drop it back to "shop default".
        selectedModel(model, previous) {
            if (this.form.quality && !this.qualityOptions.includes(this.form.quality)) {
                this.form.quality = null;
            }
            if (this.form.resolution_cap && !this.resolutionCapOptions.includes(this.form.resolution_cap)) {
                this.form.resolution_cap = null;
            }
            // A switch between two models presets the levers the new one offers with
            // what would run anyway (CMS default, else model default, see EX_GM_Model).
            // Not on load: there null deliberately means "this template doesn't decide".
            if (!model || !previous || previous.key === model.key) return;
            if (!this.form.quality && model.qualityDefault) {
                this.form.quality = model.qualityDefault;
            }
            if (!this.form.resolution_cap && model.resolutionCapDefault) {
                this.form.resolution_cap = model.resolutionCapDefault;
            }
        },
        // Function-form watchers: debouncedFetchCeiling is assigned in created(),
        // which runs after initWatch resolves string handler names.
        'form.model_key'() { this.debouncedFetchCeiling(); },
        'form.quality'() { this.debouncedFetchCeiling(); },
        'form.resolution_cap'() { this.debouncedFetchCeiling(); },
        referencesSignature() { this.debouncedFetchCeiling(); },
        cmsId() { this.debouncedFetchCeiling(); },
    },

    created() {
        this.debouncedFetchCeiling = _.debounce(this.fetchCeiling, 300);
    },

    methods: {
        // The ceiling depends on which cell of the model's price table the resolved
        // levers land on, so every lever change is a new estimate.
        async fetchCeiling() {
            const token = ++this.ceilingRequestToken;

            if (!this.cmsId || !this.form.model_key) {
                this.fetchedCeiling = null;
                return;
            }

            try {
                const ceiling = await api.costCeiling(this.cmsId, this.form.model_key, 1, {
                    quality: this.form.quality,
                    resolution_cap: this.form.resolution_cap,
                }, this.form.references.length);

                // A slower, now-stale request must not overwrite a fresher result.
                if (token === this.ceilingRequestToken) {
                    this.fetchedCeiling = ceiling;
                }
            } catch (error) {
                if (token === this.ceilingRequestToken) {
                    this.fetchedCeiling = null;
                }
            }
        },
        emptyForm() {
            return {
                id: null,
                name: '',
                context: 'auto',
                intent: '',
                prompt: '',
                model_key: null,
                // null = this template doesn't decide; the shop default applies.
                quality: null,
                resolution_cap: null,
                references: [],
            };
        },
        async onOpen() {
            this.resetState();

            if (this.template) {
                this.form = {
                    id: this.template.id,
                    name: this.template.name || '',
                    context: this.template.context || 'auto',
                    intent: this.template.intent || '',
                    prompt: this.template.prompt || '',
                    model_key: this.template.model_key || null,
                    quality: this.template.quality || null,
                    resolution_cap: this.template.resolution_cap || null,
                    references: _.map(this.template.references || [], reference => ({
                        file: _.cloneDeep(reference.file),
                        role: reference.role || '',
                    })),
                };
                this.resolvedContext = this.template.resolved_context || null;
                this.testing.previewUrl = _.get(this.template, 'testFile.url') || null;
                // The stored prompt belongs to the stored references.
                this.phrasedReferencesSignature = this.referencesSignature;
            }

            this.loadingModels = true;
            try {
                this.models = await api.fetchModels('image-edit', this.cmsId);
            } catch (error) {
                this.models = [];
            } finally {
                this.loadingModels = false;
            }

            // Preselect the CMS default for NEW templates (same source the image-edit
            // drawer uses) so the picker never shows an empty placeholder. Only a model
            // the picker actually offers: a single-image model (flux-kontext, first in
            // config order) is filtered out once the template has references, and the
            // modelOptions watcher cannot correct a value assigned after it ran.
            if (!this.form.model_key) {
                let cmsDefault = null;
                try {
                    cmsDefault = _.get(await api.fetchSettings(this.cmsId), 'default_image_edit_model_key');
                } catch (error) {
                    cmsDefault = null;
                }
                const offered = this.modelOptions.some(model => model.key === cmsDefault);
                // Without a CMS default, start on a multi-image model: templates exist for
                // references, and a single-image model would drop out of the picker on
                // the first one added.
                const fallback = this.modelOptions.find(model => model.supportsReferences) || this.modelOptions[0];
                this.form.model_key = offered ? cmsDefault : _.get(fallback, 'key', null);
            }
        },
        onClose() {
            this.unsubscribe();
        },
        onResultLoad(event) {
            // Capture the generated image's real dimensions so PhotoSwipe can size
            // and zoom it correctly (the preview keeps the sample's aspect ratio).
            const image = event.target;
            this.testing.previewWidth = image.naturalWidth || null;
            this.testing.previewHeight = image.naturalHeight || null;
        },
        openResultLightbox() {
            if (!this.testing.previewUrl) return;
            this.lightboxOpen = true;
        },
        resetState() {
            this.form = this.emptyForm();
            this.resolvedContext = null;
            this.phrasedReferencesSignature = null;
            this.phrasing = false;
            this.saving = false;
            this.testing = { running: false, progress: 0, previewUrl: null, previewWidth: null, previewHeight: null };
            this.sample = null;
            this.libraryTarget = null;
            this.lightboxOpen = false;
        },
        onContextChange() {
            // An explicit choice IS the resolved flavor; 'auto' resolves on phrasing.
            this.resolvedContext = this.form.context === 'auto' ? null : this.form.context;
        },
        referenceKey(reference) {
            return 'reference-' + _.get(reference, 'file.id');
        },
        referenceThumb(file) {
            return proxiedFileUrl(file) || _.get(file, 'url') || '';
        },
        // The COLLAPSED select value only — the expanded list renders
        // <model-select-option>, which owns the two-line layout.
        modelLabel(model) {
            const price = preciseMoneyFormat(model.effectivePrice);
            const unit = this.$tc('unit.' + model.unit, 1);
            return `${model.label} · ${this.$t('up to')} ${price}/${unit}`;
        },
        async phrase(rephrase) {
            if (!this.canPhrase) return;

            this.phrasing = true;
            try {
                const result = await api.phraseTemplatePrompt({
                    cms_id: this.cmsId,
                    intent: this.form.intent,
                    name: this.form.name || null,
                    context: this.form.context,
                    reference_roles: this.form.references.map(reference => reference.role.trim()),
                    current_prompt: rephrase ? this.form.prompt : null,
                });
                this.form.prompt = result.prompt || '';
                if (result.resolved_context) {
                    this.resolvedContext = result.resolved_context;
                }
                this.phrasedReferencesSignature = this.referencesSignature;
            } catch (error) {
                this.notifyError(this.$t('Prompt could not be phrased'), String(_.get(error, 'message') || error));
            } finally {
                this.phrasing = false;
            }
        },
        openLibrary(target) {
            this.libraryTarget = target;
            this.libraryDialogVisible = true;
        },
        onLibrarySelect(payload) {
            this.libraryDialogVisible = false;
            if (!payload) return;

            // The dialog emits an image-shaped object whose `source` is the REAL
            // library File — that is what templates reference / tests run on.
            const file = _.get(payload, 'source');
            if (!file || !file.id) return;

            if (this.libraryTarget === 'sample') {
                this.sample = {
                    fileId: parseInt(file.id, 10),
                    url: proxiedFileUrl(file) || file.url,
                    width: parseInt(_.get(payload, 'width'), 10) || null,
                    height: parseInt(_.get(payload, 'height'), 10) || null,
                };
                this.testing.previewUrl = null;
                return;
            }

            if (this.form.references.length >= 4) return;
            const exists = this.form.references.some(reference => String(_.get(reference, 'file.id')) === String(file.id));
            if (exists) return;
            this.form.references.push({ file: _.cloneDeep(file), role: '' });
        },
        moveReference(index, direction) {
            const target = index + direction;
            if (target < 0 || target >= this.form.references.length) return;
            const references = this.form.references.slice();
            const moved = references.splice(index, 1)[0];
            references.splice(target, 0, moved);
            this.form.references = references;
        },
        removeReference(index) {
            this.form.references.splice(index, 1);
        },
        buildTemplateInput() {
            return {
                id: this.form.id,
                name: this.form.name,
                intent: this.form.intent,
                prompt: this.form.prompt,
                context: this.form.context,
                resolved_context: this.resolvedContext || undefined,
                model_key: this.form.model_key,
                quality: this.form.quality || null,
                resolution_cap: this.form.resolution_cap ? parseInt(this.form.resolution_cap, 10) : null,
                references: this.form.references.map((reference, index) => ({
                    file: { id: parseInt(_.get(reference, 'file.id'), 10) },
                    role: reference.role.trim(),
                    position: index,
                })),
            };
        },
        async save(close) {
            if (!this.canSave) return;

            this.saving = true;
            try {
                const saved = await this.promptTemplateStore.savePromptTemplate(this.buildTemplateInput());
                // Replace the form with the save response (project rule) so all
                // fields round-trip.
                this.form.id = saved.id;
                this.resolvedContext = saved.resolved_context || this.resolvedContext;
                this.$emit('saved', saved);

                if (close) {
                    this.notifySuccess(this.$t('Template saved'));
                    this.$emit('update:visible', false);
                }

                return saved;
            } catch (error) {
                this.notifyError(this.$t('Template could not be saved'), String(_.get(error, 'message') || error));
                throw error;
            } finally {
                this.saving = false;
            }
        },
        async startTest() {
            if (!this.canTest) return;

            this.testing.running = true;
            this.testing.progress = 0;
            this.testing.previewUrl = null;

            try {
                // The template must exist server-side (the job stamps last_tested_at
                // and attaches the preview to it) — auto-save first.
                await this.save(false);

                await api.testPromptTemplate({
                    template_id: this.form.id,
                    prompt: this.form.prompt,
                    model_key: this.form.model_key,
                    source_file_id: this.sample.fileId,
                    source_width: this.sample.width,
                    source_height: this.sample.height,
                });

                this.subscribeToProgress();
            } catch (error) {
                this.testing.running = false;
                this.notifyError(this.$t('An error occurred'), String(_.get(error, 'message') || error));
            }
        },
        subscribeToProgress() {
            if (!Echo.client) Echo.initialize();
            this.unsubscribe();

            // The test logs against the sample File → existing per-file channel.
            this.channelName = `generative-media.file.${this.sample.fileId}`;
            const channel = Echo.client.private(this.channelName);
            channel.listen('.task:progress', (payload) => {
                this.testing.progress = payload.progress || 0;
            });
            channel.listen('.task:completed', (payload) => {
                this.testing.running = false;
                this.testing.progress = 100;
                this.testing.previewUrl = payload.previewUrl || null;
                this.notifySuccess(this.$t('Test completed'));
            });
            channel.listen('.task:failed', (payload) => {
                this.testing.running = false;
                this.notifyError(this.$t('Test failed'), payload.error || '');
            });
        },
        unsubscribe() {
            if (Echo.client && this.channelName) {
                Echo.client.leave(this.channelName);
                this.channelName = null;
            }
        },
        notifySuccess(title) {
            this.$notify.success({
                title,
                iconClass: 'fas el-icon-fa-check',
                position: 'bottom-left',
                customClass: 'dark',
                duration: 8000,
            });
        },
        notifyError(title, message) {
            this.$notify.error({
                title,
                message,
                iconClass: 'fas el-icon-fa-times',
                position: 'bottom-left',
                customClass: 'dark',
                duration: 8000,
            });
        },
    },

    beforeDestroy() {
        this.debouncedFetchCeiling.cancel();
        this.unsubscribe();
    },
};
</script>

<style lang="stylus" scoped>
// Guided-workflow treatment: a numbered step rail (1 Beschreiben, 2 Referenzen,
// 3 Prompt, 4 Test) with a hairline connector. Colours come from the Element UI
// primary palette this admin already uses everywhere ($--color-primary #409EFF
// and its standard light/active shades) — no custom brand, no new colours.
$accent = #409EFF
$accent-dark = #3a8ee6
$accent-tint = #ecf5ff
$accent-line = #d9ecff
$ink = #303133
$muted = #909399
$line = #EBEEF5

.template-drawer
    // The global `#app .full-height-drawer .drawer-body { padding: 0 }` rule wins
    // any class-based specificity (ID selector), so the padding lives on the
    // inner wrapper instead of fighting the cascade.
    .td-steps
        padding 20px 28px 8px

    >>> .drawer-footer
        display flex
        justify-content flex-end
        gap 8px

    // ===== step rail =====
    .td-steps
        display flex
        flex-direction column
        box-sizing border-box

    .td-step
        position relative
        display flex
        gap 16px
        padding-bottom 28px

        // hairline connecting the step markers
        &:not(.td-step--last)::before
            content ''
            position absolute
            left 13px
            top 32px
            bottom 4px
            width 1px
            background linear-gradient(to bottom, $accent-line, $line)

    .td-step__marker
        flex 0 0 27px
        width 27px
        height 27px
        display flex
        align-items center
        justify-content center
        border-radius 9px
        background $accent-tint
        color $accent
        font-size 12px
        font-weight 700
        line-height 1
        user-select none

    .td-step__content
        flex 1 1 auto
        min-width 0
        padding-top 4px

    .td-step__title
        margin 0 0 14px
        font-size 13px
        font-weight 700
        letter-spacing .03em
        text-transform uppercase
        color $ink

    .td-step__optional
        margin-left 8px
        font-size 10px
        font-weight 600
        letter-spacing .05em
        text-transform uppercase
        color $muted
        background #F4F4F7
        border-radius 4px
        padding 2px 6px
        vertical-align 1px

    // ===== fields =====
    .td-field
        margin-bottom 16px
        line-height 1.4

        &:last-child
            margin-bottom 0

    .td-label
        display block
        margin-bottom 6px
        font-size 12px
        font-weight 600
        color #606266

    .td-required
        color #F56C6C
        margin-left 3px

    .td-hint
        margin 8px 0 0
        font-size 12px
        line-height 1.5
        color $muted

    .td-detected
        display inline-flex
        align-items center
        gap 6px
        margin-top 10px
        padding 3px 10px
        border-radius 20px
        background $accent-tint
        color $accent
        font-size 12px
        font-weight 600
        line-height 1.4

        i
            font-size 11px

    .td-alert
        display flex
        align-items center
        gap 8px
        margin-top 10px
        padding 8px 12px
        border-radius 8px
        background #FDF6EC
        border 1px solid #FAECD8
        color #E6A23C
        font-size 12px
        line-height 1.4

    // ===== step 2: reference filmstrip =====
    .td-refs__list
        display flex
        flex-direction column
        gap 10px

    .td-ref
        display flex
        align-items center
        gap 14px
        padding 10px
        box-sizing border-box
        background #fff
        border 1px solid $line
        border-radius 10px
        transition border-color .18s ease, box-shadow .18s ease

        &:hover
            border-color $accent-line
            box-shadow 0 2px 10px rgba(110, 38, 255, .07)

    .td-ref__visual
        position relative
        flex 0 0 64px

    .td-ref__thumb
        display block
        width 64px
        height 64px
        border-radius 8px
        object-fit cover
        background #f4f5f7

    .td-ref__badge
        position absolute
        left -6px
        bottom -6px
        padding 2px 7px
        border-radius 6px
        background $accent
        color #fff
        font-size 10px
        font-weight 700
        letter-spacing .02em
        box-shadow 0 1px 4px rgba(20, 20, 28, .25)
        white-space nowrap

    .td-ref__body
        flex 1 1 auto
        min-width 0

    .td-ref__name
        margin-top 5px
        font-size 11px
        color $muted
        line-height 1.4
        overflow hidden
        white-space nowrap
        text-overflow ellipsis

    .td-ref__actions
        flex 0 0 auto
        display flex
        align-items center

    .td-ref-add
        display flex
        align-items center
        justify-content center
        gap 8px
        width 100%
        box-sizing border-box
        margin-top 10px
        padding 11px
        background transparent
        border 1px dashed #D5D5DE
        border-radius 10px
        color #606266
        font-size 12px
        font-weight 600
        cursor pointer
        transition border-color .18s ease, color .18s ease, background .18s ease

        &:hover
            border-color $accent
            color $accent
            background rgba(110, 38, 255, .03)

        i
            font-size 14px

    // ===== step 3: phrase + prompt =====
    .td-phrase
        display flex
        align-items center
        justify-content center
        gap 8px
        width 100%
        box-sizing border-box
        padding 11px
        border none
        border-radius 9px
        background $accent
        color #fff
        font-size 13px
        font-weight 600
        cursor pointer
        transition background .18s ease, box-shadow .18s ease

        &:hover:not(:disabled)
            background $accent-dark
            box-shadow 0 4px 14px rgba(110, 38, 255, .25)

        &:disabled
            opacity .45
            cursor not-allowed

        &.is-busy
            background $accent-dark

    .td-prompt
        margin-top 14px

        >>> .el-textarea__inner
            background #FBFAFE
            border-color $accent-line
            line-height 1.55

            &:focus
                border-color $accent

    .td-prompt__foot
        display flex
        align-items flex-start
        justify-content space-between
        gap 12px
        margin-top 6px

        .td-hint
            margin 0

    .td-ghost
        flex 0 0 auto
        display inline-flex
        align-items center
        gap 5px
        padding 4px 10px
        border 1px solid $line
        border-radius 6px
        background #fff
        color #606266
        font-size 12px
        cursor pointer
        transition border-color .18s ease, color .18s ease

        &:hover:not(:disabled)
            border-color $accent
            color $accent

        &:disabled
            opacity .5
            cursor not-allowed

    .td-field--model
        margin-bottom 14px

    // ===== step 4: test bench =====
    .td-bench
        display flex
        align-items stretch
        gap 10px

    .td-bench__pane
        position relative
        flex 1 1 0
        min-width 0
        box-sizing border-box
        height 210px
        display flex
        align-items center
        justify-content center
        background #F7F7FA
        border 1px dashed #D5D5DE
        border-radius 10px
        overflow hidden
        cursor pointer
        transition border-color .18s ease

        img
            max-width 100%
            max-height 100%
            object-fit contain

        // the requested hover affordance on the sample dropzone
        &:hover
            border-color $accent

            .td-bench__empty
                color $accent

        &--result
            cursor default
            border-style solid
            border-color $line

            &:hover
                border-color $line

                .td-bench__empty
                    color #c0c4cc

            &.is-zoomable
                cursor zoom-in

                &:hover .td-bench__zoom
                    opacity 1

    .td-bench__zoom
        position absolute
        inset 0
        z-index 2
        display flex
        flex-direction column
        align-items center
        justify-content center
        gap 6px
        background rgba(20, 20, 28, .42)
        color #fff
        font-size 12px
        font-weight 600
        opacity 0
        transition opacity .18s ease
        pointer-events none

        i
            font-size 22px

    .td-bench__tag
        position absolute
        top 8px
        left 8px
        z-index 2
        padding 2px 8px
        border-radius 5px
        background rgba(255, 255, 255, .92)
        box-shadow 0 1px 3px rgba(20, 20, 28, .12)
        color #606266
        font-size 10px
        font-weight 700
        letter-spacing .04em
        text-transform uppercase

    .td-bench__arrow
        flex 0 0 auto
        display flex
        align-items center
        color #C0C4CC
        font-size 16px
        transition color .18s ease

    .td-bench.is-running
        .td-bench__arrow
            color $accent
            animation td-nudge 1.1s ease-in-out infinite

        .td-bench__pane--result
            border-color $accent-line

    .td-bench__empty
        display flex
        flex-direction column
        align-items center
        gap 7px
        color #c0c4cc
        font-size 12px
        font-weight 600
        transition color .18s ease

        i
            font-size 22px

    .td-bench__progress
        position relative
        z-index 1
        display flex
        align-items center
        justify-content center

    .td-bench-actions
        display flex
        align-items center
        gap 12px
        margin-top 12px

    .td-cost
        display flex
        flex-direction column
        align-items flex-end
        line-height 1.25
        margin-left auto

    .td-cost__label
        font-size 11px
        color $muted

    .td-cost__amount
        font-size 15px
        font-weight 700
        color $ink

    // ===== transitions =====
    .td-fade-enter-active
    .td-fade-leave-active
        transition opacity .18s ease, transform .18s ease

    .td-fade-enter
    .td-fade-leave-to
        opacity 0
        transform translateY(4px)

@keyframes td-nudge
    0%, 100%
        transform translateX(0)
    50%
        transform translateX(3px)
</style>
